/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, LinearProgress, Theme } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import { History } from "history";
import React, { FC, useEffect } from "react";
import { useDispatch } from "react-redux";
import { acknowledgePublishError, acknowledgePublishSuccess, cancelPublish, SubmitState } from "../../redux";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialog: {
      // width: "50%",
    }
  })
);

interface SubmitOverlayProps extends SubmitState {
  onFinishedPrompts: () => void;
  onSubmitted: () => void;
  history: History<any>;
}

const SubmitOverlay: FC<SubmitOverlayProps> = props => {
  const classes = useStyles(props);
  const { step, submitting, onFinishedPrompts, onSubmitted, error } = props;

  useEffect(
    function notifyPromptsDone() {
      // TODO: Handle prompts to collect data
      if (step === "prompts") onFinishedPrompts();
    },
    [step, onFinishedPrompts]
  );

  const dispatch = useDispatch();

  function handleAcknowledgeError() {
    dispatch(acknowledgePublishError());
  }

  function handleCancel() {
    dispatch(cancelPublish());
  }

  function handleSubmitted() {
    dispatch(acknowledgePublishSuccess());
    onSubmitted();
  }

  let submitProgressMessage: string;
  let actions: [string, () => void][] = [["Cancel", handleCancel]];
  let contentMessage: string | React.ReactNode | undefined;

  let progress: number | undefined;
  if (error) {
    submitProgressMessage = "Error";
    contentMessage = error;
    actions = [["OK", handleAcknowledgeError]];
  } else {
    switch (step) {
      case "network-test":
        progress = 10;
        submitProgressMessage = "Checking network...";
        break;
      case "cleanup":
        progress = 20;
        submitProgressMessage = "Cleaning up...";
        break;
      case "prompts":
        progress = 60;
        submitProgressMessage = "Collecting data...";
        break;
      case "submitting":
        progress = 90;
        submitProgressMessage = "Finalizing...";
        break;
      case "submitted":
        progress = 100;
        submitProgressMessage = "Complete!";
        actions = [["OK", handleSubmitted]];
        break;
      default:
        submitProgressMessage = "";
        break;
    }
  }

  return (
    <Dialog open={submitting} fullWidth={true} className={classes.dialog}>
      <DialogTitle>{submitProgressMessage}</DialogTitle>
      <DialogContent>
        {contentMessage && (
          <DialogContentText>{contentMessage}</DialogContentText>
        )}
        {progress && <LinearProgress variant="determinate" value={progress} />}
      </DialogContent>
      {actions.length && (
        <DialogActions>
          {actions.map(([title, handler], idx) => (
            <Button key={idx} onClick={handler}>
              {title}
            </Button>
          ))}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SubmitOverlay;
